import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { withRouter } from 'react-router-dom'

import { Alert, Button, Divider, Icon, Modal, Row, Select, Typography, notification } from 'antd'
import { Formik } from 'formik'
import _ from 'lodash'
import moment from 'moment'
import { v4 as uuidV4 } from 'uuid'

import { ConfirmLeaveWithoutSavingModal } from '../../../components/ConfirmLeaveWithoutSavingModal'
import CustomInput from '../../../components/CustomInput/customInput'
import FormHeader from '../../../components/FormHeader/FormHeader'
import { api, request } from '../../../util/handlerRequestUtil'
import { getMessage } from '../../../util/IntlMessages'
import Forecast from '../models/Forecast'
import CommercialProcessesItems from './items/CommercialProcessesItems'
import SoyAreaItems, { getPaymentMonthKey } from './items/SoyAreaItems'

async function storeForecasts(data) {
  try {
    const forecast = await request(api.storeForecasts(data))
    notification.success({
      description: getMessage('successStoreForecasts'),
      message: getMessage('sweetAlerts.success')
    })

    return forecast
  } catch (error) {
    console.tron.log(error)
    throw error
  }
}

async function updateForecasts(data, id) {
  try {
    await request(api.updateForecasts(id, data))
    notification.success({
      description: getMessage('successUpdateForecasts'),
      message: getMessage('sweetAlerts.success')
    })
  } catch (error) {
    console.tron.log(error)
    throw error
  }
}

async function checkAction(payload) {
  try {
    const res = await request(api.checkForecastAction(payload))
    return res
  } catch (error) {
    console.tron.log(error)
    throw error
  }
}

async function getForecastReferencePeriodById(referencePeriodId) {
  try {
    const res = await request(api.getForecastReferencePeriodById(referencePeriodId))
    return res
  } catch (error) {
    console.tron.log(error)
    throw error
  }
}

const CreateForecasts = function ({ customerId, districts, history }) {
  const [soyAreaItems, setSoyAreaItems] = useState([])
  const [commercialProcessesItems, setCommercialProcessesItems] = useState([])
  const [formValues, setFormValues] = useState({})
  const [forecastTypeReservedId, setForecastTypeReservedId] = useState(null)
  const [selectedDistrict, setSelectedDistrict] = useState(undefined)
  const [referencePeriods, setReferencePeriods] = useState([])
  const [referencePeriodsDetails, setReferencePeriodsDetails] = useState({})
  const [selectedReferencePeriod, setSelectedReferencePeriod] = useState(undefined)
  const [filtersTouched, setFiltersTouched] = useState(false)
  const [visitModalVisible, setVisitModalVisible] = useState(false)
  const [visitActionMode, setVisitActionMode] = useState('create')
  const [visitPayload, setVisitPayload] = useState(null)
  const [isLoadingReferencePeriodsDetails, setIsLoadingReferencePeriodsDetails] = useState(false)
  const [loadedSnapshot, setLoadedSnapshot] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [forceVisibilityModalOpen, setForceVisibilityModalOpen] = useState(false)

  const submitFormRef = useRef(null)

  const allowedReferencePeriods = useMemo(
    () => (referencePeriods || []).filter((item) => ['Safra 2025/2026', 'Safra 2026/2027'].includes(item.description)),
    [referencePeriods]
  )

  const detailedAllowedReferencePeriods = useMemo(() => {
    return allowedReferencePeriods
      .map((item) => {
        const details = referencePeriodsDetails[item.value]

        if (!details) {
          return null
        }

        return {
          id: details.id,
          description: item.description,
          beginDate: details.begin_date,
          endDate: details.end_date
        }
      })
      .filter(Boolean)
      .sort((firstItem, secondItem) => moment(firstItem.beginDate).diff(moment(secondItem.beginDate)))
  }, [allowedReferencePeriods, referencePeriodsDetails])

  const selectedReferencePeriodDetails = useMemo(() => {
    if (!selectedReferencePeriod) {
      return null
    }

    return referencePeriodsDetails[selectedReferencePeriod] || null
  }, [selectedReferencePeriod, referencePeriodsDetails])

  const currentDate = useMemo(() => moment().startOf('day'), [])

  const currentReferencePeriod = useMemo(() => {
    return detailedAllowedReferencePeriods.find((referencePeriod) => {
      const beginDate = moment(referencePeriod.beginDate).startOf('day')
      const endDate = moment(referencePeriod.endDate).startOf('day')

      return currentDate.isBetween(beginDate, endDate, 'day', '[]')
    })
  }, [detailedAllowedReferencePeriods, currentDate])

  const nextReferencePeriod = useMemo(() => {
    if (!currentReferencePeriod) {
      return null
    }

    const currentReferencePeriodIndex = detailedAllowedReferencePeriods.findIndex(
      (referencePeriod) => referencePeriod.id === currentReferencePeriod.id
    )

    if (currentReferencePeriodIndex < 0) {
      return null
    }

    return detailedAllowedReferencePeriods[currentReferencePeriodIndex + 1] || null
  }, [detailedAllowedReferencePeriods, currentReferencePeriod])

  const referencePeriodValidation = useMemo(() => {
    if (!selectedReferencePeriodDetails) {
      return {
        isValid: false,
        shouldDisableButton: true,
        shouldUseBeginDateOnCreate: false,
        message: 'Selecione a safra para continuar'
      }
    }

    const selectedBeginDate = moment(selectedReferencePeriodDetails.begin_date).startOf('day')
    const selectedEndDate = moment(selectedReferencePeriodDetails.end_date).startOf('day')

    const isSelectedReferencePeriodCurrent = currentDate.isBetween(selectedBeginDate, selectedEndDate, 'day', '[]')

    if (isSelectedReferencePeriodCurrent) {
      return {
        isValid: true,
        shouldDisableButton: false,
        shouldUseBeginDateOnCreate: false,
        message: ''
      }
    }

    if (currentReferencePeriod && nextReferencePeriod && nextReferencePeriod.id === selectedReferencePeriodDetails.id) {
      const shouldUseBeginDateOnCreate = currentDate.isBefore(
        moment(nextReferencePeriod.beginDate).startOf('day'),
        'day'
      )

      return {
        isValid: true,
        shouldDisableButton: false,
        shouldUseBeginDateOnCreate,
        message: ''
      }
    }

    return {
      isValid: false,
      shouldDisableButton: true,
      shouldUseBeginDateOnCreate: false,
      message: 'Não é possível cadastrar Forecast para uma Safra fora do período.'
    }
  }, [selectedReferencePeriodDetails, currentDate, currentReferencePeriod, nextReferencePeriod])

  const areaIntactaLegal = useMemo(
    () =>
      soyAreaItems
        .filter((item) => item.forecastTypeId === forecastTypeReservedId)
        .reduce((acc, item) => acc + parseInt(item.area || 0), 0),
    [soyAreaItems, forecastTypeReservedId]
  )
  const areaTotal = useMemo(() => soyAreaItems.reduce((acc, item) => acc + parseInt(item.area || 0), 0), [soyAreaItems])
  const commercialProcessesTotalArea = useMemo(
    () => commercialProcessesItems.reduce((acc, item) => acc + parseInt(item.area || 0), 0),
    [commercialProcessesItems]
  )
  const hasMultipleDistricts = (districts || []).length > 1
  const hasDistrictAvailable = hasMultipleDistricts ? !!selectedDistrict : (districts || []).length === 1
  const selectedDistrictDescription = useMemo(
    () => (districts || []).find((district) => district.value === selectedDistrict)?.description,
    [selectedDistrict, districts]
  )
  const selectedReferencePeriodDescription = useMemo(
    () => (allowedReferencePeriods || []).find((item) => item.value === selectedReferencePeriod)?.description,
    [allowedReferencePeriods, selectedReferencePeriod]
  )

  const currentSnapshot = useMemo(() => {
    if (!visitModalVisible)
      return JSON.stringify({
        soyAreaItems,
        commercialProcessesItems
      })
  }, [soyAreaItems, commercialProcessesItems, visitModalVisible])

  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (visitModalVisible) return
    setHasChanges(
      loadedSnapshot !== null && currentSnapshot !== loadedSnapshot && !!selectedDistrict && !!selectedReferencePeriod
    )
  }, [loadedSnapshot, currentSnapshot, selectedDistrict, selectedReferencePeriod, visitModalVisible])

  const openVisitModal = useCallback((payload, mode = 'create') => {
    setHasChanges(false)
    setVisitPayload(payload)
    setVisitActionMode(mode)
    setVisitModalVisible(true)
  }, [])

  const closeVisitModal = useCallback(() => {
    setHasChanges(false)
    setVisitModalVisible(false)
    setVisitActionMode('create')
    setVisitPayload(null)
  }, [])

  const handleSaveAndStay = useCallback(async () => {
    setIsSubmitting(true)
    setForceVisibilityModalOpen(false)
    try {
      if (submitFormRef.current) {
        await submitFormRef.current()
      }
    } catch (error) {
      console.tron.log(error)
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const handleConfirmVisitAction = useCallback(() => {
    if (visitActionMode === 'update' && visitPayload?.actionTypeId && visitPayload?.actionId) {
      history.push(`/actions/${visitPayload.actionTypeId}/${visitPayload.actionId}`)
      closeVisitModal()
      return
    }

    history.push('/actions/create', {
      source: 'forecast-visit-modal',
      preloadActionData: {
        baseFields: {
          actionTypeId: visitPayload?.actionTypeId ?? null,
          actionStatusId: visitPayload?.actionStatusId ?? null,
          customerId: Number(visitPayload?.customerId) || null,
          memberId: visitPayload?.memberId ?? null,
          locationId: visitPayload?.locationId ?? null,
          referencePeriodId: visitPayload?.referencePeriodId ?? null,
          executionDate: moment().toISOString(),
          active: true
        },
        customFields: {}
      }
    })

    closeVisitModal()
  }, [visitActionMode, visitPayload, history, closeVisitModal])

  const handleBackFilters = useCallback(() => {
    if (hasChanges) setForceVisibilityModalOpen(true)
    else handleBackFiltersWithoutSave()
  }, [hasMultipleDistricts, hasChanges])

  function handleBackFiltersWithoutSave() {
    setForceVisibilityModalOpen(false)
    setFiltersTouched(false)
    setFormValues({})
    setSoyAreaItems([])
    setCommercialProcessesItems([])
    setLoadedSnapshot(null)

    if (hasMultipleDistricts) {
      setSelectedDistrict(undefined)
    }

    setSelectedReferencePeriod(undefined)
  }

  const handleSubmit = useCallback(
    async (values) => {
      if (referencePeriodValidation.shouldDisableButton) {
        notification.warning({
          message: 'Atenção',
          description: 'Não é possível cadastrar Forecast para uma Safra fora do período.'
        })
        return
      }

      const id = parseInt(values.id) || null
      const formattedSoy = formatObjects(soyAreaItems)
      const formattedCommercial = formatObjects(commercialProcessesItems)
      const forecast = new Forecast({
        ...values,
        customerId,
        referencePeriodsId: selectedReferencePeriod,
        soyAreaItems: formattedSoy,
        commercialProcessesItems: formattedCommercial,
        areaTotal,
        areaIntactaLegal,
        commercialProcessesTotalArea
      })

      const data = forecast.toApi()

      try {
        if (!forecast.id) {
          const forecastDateForCreation = referencePeriodValidation.shouldUseBeginDateOnCreate
            ? moment(selectedReferencePeriodDetails.begin_date).format('YYYY-MM-DD')
            : new Date().toISOString().split('T')[0]

          const forecastCreateDto = {
            customerId,
            forecastDate: forecastDateForCreation,
            districtId: selectedDistrict,
            referencePeriodsId: selectedReferencePeriod,
            active: true,
            deleted: false,
            forecastCommercialProcessesItem: data.commercialProcessesItems,
            forecastItems: data.soyAreaItems
          }

          const createdForecast = await storeForecasts(forecastCreateDto)
          setFormValues({ ...data, id: createdForecast.id, forecastDate: forecastDateForCreation })
          setLoadedSnapshot(currentSnapshot)

          const checkPayload = {
            forecastId: createdForecast.id,
            customerId,
            districtId: selectedDistrict,
            safraId: selectedReferencePeriod
          }

          const checkResult = await checkAction(checkPayload)

          openVisitModal(
            {
              ...checkPayload,
              actionTypeId: checkResult?.actionTypeId ?? null,
              actionStatusId: checkResult?.actionStatusId ?? null,
              referencePeriodId: checkResult?.referencePeriodId ?? selectedReferencePeriod ?? null,
              actionId: checkResult?.actionId ?? null,
              memberId: checkResult?.memberId ?? null,
              locationId: checkResult?.locationId ?? null,
              regionId: checkResult?.regionId ?? null,
              exists: checkResult?.exists ?? false,
              message: checkResult?.message ?? null
            },
            checkResult?.exists ? 'update' : 'create'
          )
        } else {
          const forecastUpdateDto = {
            customerId,
            forecastDate: formValues.forecastDate,
            districtId: selectedDistrict,
            referencePeriodsId: selectedReferencePeriod,
            forecastCommercialProcessesItem: data.commercialProcessesItems,
            forecastItems: data.soyAreaItems
          }

          await updateForecasts(forecastUpdateDto, id)
          await retrieveForecasts(customerId, selectedDistrict, selectedReferencePeriod)
          setHasChanges(false)
          const checkPayload = {
            forecastId: id,
            customerId,
            districtId: selectedDistrict,
            safraId: selectedReferencePeriod
          }

          const checkResult = await checkAction(checkPayload)

          openVisitModal(
            {
              ...checkPayload,
              actionTypeId: checkResult?.actionTypeId ?? null,
              actionStatusId: checkResult?.actionStatusId ?? null,
              referencePeriodId: checkResult?.referencePeriodId ?? selectedReferencePeriod ?? null,
              actionId: checkResult?.actionId ?? null,
              memberId: checkResult?.memberId ?? null,
              locationId: checkResult?.locationId ?? null,
              regionId: checkResult?.regionId ?? null,
              exists: checkResult?.exists ?? false,
              message: checkResult?.message ?? null
            },
            checkResult?.exists ? 'update' : 'create'
          )
        }
      } catch (error) {
        console.tron.log(error)
      }
    },
    [
      soyAreaItems,
      commercialProcessesItems,
      areaTotal,
      areaIntactaLegal,
      commercialProcessesTotalArea,
      selectedDistrict,
      selectedReferencePeriod,
      selectedReferencePeriodDetails,
      customerId,
      formValues,
      openVisitModal,
      referencePeriodValidation
    ]
  )

  useEffect(() => {
    if (customerId && selectedDistrict && selectedReferencePeriod) {
      retrieveForecasts(customerId, selectedDistrict, selectedReferencePeriod)
      getForecastsType()
    } else {
      setFormValues({})
      setSoyAreaItems([])
      setCommercialProcessesItems([])
    }
  }, [customerId, selectedDistrict, selectedReferencePeriod])

  useEffect(() => {
    if (!selectedDistrict && districts.length === 1) {
      setSelectedDistrict(districts[0].value)
    }
  }, [selectedDistrict, districts])

  useEffect(() => {
    loadReferencePeriods()
  }, [])

  useEffect(() => {
    if (!filtersTouched) return
    if (!hasDistrictAvailable) return
    if (!selectedReferencePeriod) {
      notification.info({
        message: 'Atenção',
        description: 'Selecione a safra para continuar'
      })
    }
  }, [filtersTouched, hasDistrictAvailable, selectedReferencePeriod])

  function formatObjects(items) {
    const newValuesFormated = []
    for (const element of items) {
      const cloned = { ...element }
      delete cloned.id
      newValuesFormated.push(cloned)
    }

    return newValuesFormated
  }

  async function loadReferencePeriods() {
    try {
      setIsLoadingReferencePeriodsDetails(true)

      const response = await request(api.listSafraDropdown({ active: true }))
      const periods = Array.isArray(response) ? response : []
      setReferencePeriods(periods)
      setSelectedReferencePeriod(undefined)

      const allowedPeriods = periods.filter((item) => ['Safra 2025/2026', 'Safra 2026/2027'].includes(item.description))

      const detailsEntries = await Promise.all(
        allowedPeriods.map(async (referencePeriod) => {
          const details = await getForecastReferencePeriodById(referencePeriod.value)
          return [referencePeriod.value, details]
        })
      )

      const detailsById = detailsEntries.reduce((acc, [referencePeriodId, details]) => {
        acc[referencePeriodId] = details
        return acc
      }, {})

      setReferencePeriodsDetails(detailsById)
    } catch (error) {
      console.tron.log(error)
      setReferencePeriods([])
      setReferencePeriodsDetails({})
      setSelectedReferencePeriod(undefined)
    } finally {
      setIsLoadingReferencePeriodsDetails(false)
    }
  }

  async function retrieveForecasts(customerId, districtId, referencePeriodsId) {
    try {
      const params = { districtId }

      if (referencePeriodsId) {
        params.referencePeriodsId = referencePeriodsId
      }

      const res = await request(api.retrieveForecasts(customerId, params))

      const retrievedFormValues = res || {}
      const retrievedSoyItems = res?.forecastItems ? res.forecastItems : []
      const retrievedCommercialItems = res?.forecastCommercialProcessesItem
        ? res.forecastCommercialProcessesItem.map((item) => ({
            ...item,
            predictedCommercialProcesses: item.predictedCommercialProcesses.split(',')
          }))
        : []

      setFormValues(retrievedFormValues)
      setSoyAreaItems(retrievedSoyItems)
      setCommercialProcessesItems(retrievedCommercialItems)

      setLoadedSnapshot(
        JSON.stringify({
          soyAreaItems: retrievedSoyItems,
          commercialProcessesItems: retrievedCommercialItems
        })
      )
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function getForecastsType() {
    const res = await request(api.getForecastsType({ active: true, name: 'RESERVED' }))
    setForecastTypeReservedId(res[0].value)
  }

  const addSoyAreaItem = useCallback(
    (newItem) => {
      const itemToUpdateIndex = soyAreaItems.findIndex((item) => {
        const sameTechnology = Number(item.tecnologiaId) === Number(newItem.tecnologiaId)

        const sameForecastType = Number(item.forecastTypeId) === Number(newItem.forecastTypeId)

        if (!sameTechnology || !sameForecastType) {
          return false
        }

        const isReserved = Number(newItem.forecastTypeId) === Number(forecastTypeReservedId)

        if (!isReserved) {
          return true
        }

        const currentPaymentMonth = getPaymentMonthKey(item.paymentMonthYear)
        const newPaymentMonth = getPaymentMonthKey(newItem.paymentMonthYear)

        return currentPaymentMonth === newPaymentMonth
      })

      const newItems = [...soyAreaItems]

      if (itemToUpdateIndex > -1) {
        newItems[itemToUpdateIndex] = {
          ...newItem,
          id: uuidV4()
        }
      } else {
        newItems.push({
          ...newItem,
          id: uuidV4()
        })
      }

      setSoyAreaItems(newItems)
    },
    [soyAreaItems, forecastTypeReservedId]
  )

  const addCommercialProcessesItem = useCallback(
    (newItem) => {
      const itemToUpdateIndex = commercialProcessesItems.findIndex(
        ({ predictedCommercialProcesses }) =>
          Array.isArray(newItem.predictedCommercialProcesses) &&
          Array.isArray(predictedCommercialProcesses) &&
          _.isEqual(newItem.predictedCommercialProcesses, predictedCommercialProcesses)
      )
      const newItems = [...commercialProcessesItems]

      if (itemToUpdateIndex > -1) {
        newItems[itemToUpdateIndex] = {
          ...newItem,
          id: uuidV4()
        }
      } else {
        newItems.push({
          ...newItem,
          id: uuidV4()
        })
      }

      setCommercialProcessesItems(newItems)
    },
    [commercialProcessesItems]
  )

  const handleDeleteSoyAreaItem = useCallback(
    async (id) => {
      try {
        await request(api.updateForecastItems(id, { last: false, active: false }))
        setSoyAreaItems(soyAreaItems.filter((item) => item.id !== id))
        notification.success({
          description: getMessage('successRemoveSoyAreaForecast'),
          message: getMessage('sweetAlerts.success')
        })
      } catch (error) {
        console.tron.log(error)
      }
    },
    [soyAreaItems]
  )

  const handleDeleteCommercialProcessesItem = useCallback(
    async (id) => {
      try {
        await request(api.updateForecastCommercialProcessesItem(id, { last: false, active: false }))
        setCommercialProcessesItems(commercialProcessesItems.filter((item) => item.id !== id))
        notification.success({
          description: getMessage('successRemoveCommercialProcesses'),
          message: getMessage('sweetAlerts.success')
        })
      } catch (error) {
        console.tron.log(error)
      }
    },
    [commercialProcessesItems]
  )

  function renderFilters() {
    const sharedStyle = {
      width: 300
    }

    if (hasMultipleDistricts) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            margin: '48px 0 24px',
            flexWrap: 'wrap'
          }}
        >
          <Select
            showSearch
            value={selectedDistrict}
            placeholder="Selecione um distrito"
            style={sharedStyle}
            onChange={(value) => {
              setFiltersTouched(true)
              setSelectedDistrict(value)
            }}
            optionFilterProp="children"
          >
            {districts.map(({ value, description }) => (
              <Select.Option key={value} value={value}>
                {description}
              </Select.Option>
            ))}
          </Select>
          <Select
            showSearch
            value={selectedReferencePeriod}
            placeholder="Selecione uma safra"
            style={sharedStyle}
            onChange={(value) => {
              setFiltersTouched(true)
              setSelectedReferencePeriod(value)
            }}
            optionFilterProp="children"
            loading={isLoadingReferencePeriodsDetails}
          >
            {allowedReferencePeriods.map(({ value, description }) => (
              <Select.Option key={value} value={value}>
                {description}
              </Select.Option>
            ))}
          </Select>
        </div>
      )
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '48px 0 24px'
        }}
      >
        <Select
          showSearch
          value={selectedReferencePeriod}
          placeholder="Selecione uma safra"
          style={sharedStyle}
          onChange={(value) => {
            setFiltersTouched(true)
            setSelectedReferencePeriod(value)
          }}
          optionFilterProp="children"
          loading={isLoadingReferencePeriodsDetails}
        >
          {allowedReferencePeriods.map(({ value, description }) => (
            <Select.Option key={value} value={value}>
              {description}
            </Select.Option>
          ))}
        </Select>
      </div>
    )
  }

  const shouldShowFilters = hasMultipleDistricts
    ? !selectedDistrict || !selectedReferencePeriod
    : !selectedReferencePeriod

  const shouldDisableSubmitButton =
    !hasDistrictAvailable ||
    !selectedReferencePeriod ||
    isLoadingReferencePeriodsDetails ||
    referencePeriodValidation.shouldDisableButton

  return (
    <div>
      {shouldShowFilters && renderFilters()}

      {!hasDistrictAvailable || !selectedReferencePeriod ? null : (
        <Formik
          enableReinitialize={true}
          initialValues={formValues}
          onSubmit={handleSubmit}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ submitForm, errors, handleBlur, setFieldValue }) => {
            submitFormRef.current = submitForm
            return (
              <div className="contacts-form">
                <Row type="flex" justify="space-between" align="center" style={{ padding: '2%' }}>
                  <Typography.Title level={4} style={{ margin: 0, lineHeight: 2 }}>
                    Distrito: {selectedDistrictDescription} | {selectedReferencePeriodDescription}
                  </Typography.Title>

                  <Row type="flex">
                    <Button className="button button-return" onClick={handleBackFilters} style={{ marginRight: 8 }}>
                      <Icon type="left" theme="outlined" className="button-icon" />
                      {getMessage('back')}
                    </Button>
                    <Button
                      type="primary"
                      className="button-action"
                      onClick={submitForm}
                      disabled={shouldDisableSubmitButton}
                      title={referencePeriodValidation.message || ''}
                    >
                      {formValues.id ? getMessage('update') : getMessage('register')}
                    </Button>
                  </Row>
                </Row>
                {referencePeriodValidation.message ? (
                  <div style={{ padding: '0 2% 16px 2%' }}>
                    <Alert type="warning" showIcon message="Atenção" description={referencePeriodValidation.message} />
                  </div>
                ) : null}
                <FormHeader
                  color="#000"
                  title={getMessage('customer.forecast.title')}
                  orientation="left"
                  type="horizontal"
                />
                <SoyAreaItems
                  soyAreaItems={soyAreaItems}
                  onAddItem={addSoyAreaItem}
                  onDeleteItem={handleDeleteSoyAreaItem}
                  handleBlur={handleBlur}
                  shouldDisableSubmitButton={shouldDisableSubmitButton}
                />
                <Divider />
                <Row type="flex" justify="start">
                  <CustomInput
                    disabled={true}
                    field="areaIntactaLegal"
                    errors={errors}
                    setFieldValue={setFieldValue}
                    rowSpan={6}
                    value={{ areaIntactaLegal }}
                    type="number"
                    defaultValue={0}
                    handleBlur={handleBlur}
                  />
                  <CustomInput
                    disabled={true}
                    field="areaTotal"
                    errors={errors}
                    setFieldValue={setFieldValue}
                    rowSpan={6}
                    value={{ areaTotal }}
                    type="number"
                    defaultValue={0}
                    handleBlur={handleBlur}
                  />
                </Row>
                <FormHeader
                  color="#000"
                  title={getMessage('customer.forecast.commercial.process.predicted.tille')}
                  orientation="left"
                  type="horizontal"
                />
                <CommercialProcessesItems
                  commercialProcessesItems={commercialProcessesItems}
                  onAddItem={addCommercialProcessesItem}
                  onDeleteItem={handleDeleteCommercialProcessesItem}
                  handleBlur={handleBlur}
                />
                <Row type="flex" justify="start">
                  <CustomInput
                    disabled={true}
                    field="commercialProcessesTotalArea"
                    errors={errors}
                    setFieldValue={setFieldValue}
                    rowSpan={6}
                    value={{ commercialProcessesTotalArea }}
                    type="number"
                    defaultValue={0}
                    handleBlur={handleBlur}
                  />
                </Row>
              </div>
            )
          }}
        </Formik>
      )}
      <ConfirmLeaveWithoutSavingModal
        when={hasChanges}
        leaveRoute="/customer"
        saveAction={handleSaveAndStay}
        isSubmitting={isSubmitting}
        forceVisibility={forceVisibilityModalOpen}
        setForceVisibility={(value) => setForceVisibilityModalOpen(value)}
        leaveWithoutSavingCleanup={handleBackFiltersWithoutSave}
      />
      <Modal
        visible={visitModalVisible}
        title={visitActionMode === 'update' ? 'Atualizar visita' : 'Criar visita'}
        onCancel={closeVisitModal}
        footer={[
          <Button key="cancel" onClick={closeVisitModal}>
            Cancelar
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmVisitAction}>
            {visitActionMode === 'update' ? 'Atualizar Visita' : 'Criar Nova Visita'}
          </Button>
        ]}
      >
        <Alert
          type="info"
          showIcon
          message={visitActionMode === 'update' ? 'Atualizar Visita' : 'Criar Nova Visita'}
          description={
            visitActionMode === 'update'
              ? 'Existe uma visita com esses dados, deseja atualizá-la?'
              : 'Deseja criar uma visita a partir dessas alterações feitas?'
          }
        />
      </Modal>
    </div>
  )
}

export default withRouter(CreateForecasts)
